import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import { bankAccountSchema } from "../../../../models/bankAccount";
import { BANK_OPTIONS, ACCOUNT_TYPES } from "../constants";
import { OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";
import PropTypes from "prop-types";

/**
 * BankAccountEditor
 * Formulário principal de Contas Bancárias (Caixas e Bancos).
 * Migrado para o Neo Design System.
 */
const BankAccountEditor = ({ account, onSave, onDelete, isSaving, onChange }) => {
    const formik = useFormik({
        initialValues: account || {},
        validationSchema: bankAccountSchema,
        enableReinitialize: true,
        onSubmit: (values) => onSave(values)
    });

    useEffect(() => {
        if (onChange) {
            onChange(formik.dirty);
        }
    }, [formik.dirty, onChange]);

    if (!account) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5" style={{ minHeight: "500px" }}>
                <div className="text-center py-5">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-bank-plus"></i>
                        </div>
                    </div>
                    <h5 className="fw-bold text-dark font-size-16" style={{ fontFamily: "Outfit, sans-serif" }}>Selecione uma conta</h5>
                    <p className="text-muted font-size-13 mx-auto" style={{ maxWidth: '320px' }}>
                        Escolha uma conta bancária ao lado ou crie uma nova para gerenciar seu financeiro.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{account.id ? formik.values.description : "Nova Conta Bancária"}</span>
                    {account.id && (
                        <NeoBadge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {formik.values.status === GLOBAL_STATUS.ACTIVE ? 'Ativa' : 'Inativa'}
                        </NeoBadge>
                    )}
                </span>
            }
            subtitle="Dados básicos e saldo inicial"
            headerIcon="mdi-bank-transfer"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {account.id && (
                        <NeoButton
                            type="button"
                            variant="outline-danger"
                            square={true}
                            onClick={() => onDelete(account.id, account.description)}
                            disabled={isSaving}
                            title="Excluir Conta"
                            icon="mdi-trash-can-outline"
                        />
                    )}
                    <NeoButton
                        variant="primary"
                        onClick={formik.handleSubmit}
                        loading={isSaving}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando..." />

            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-4 d-flex align-items-center" style={{ letterSpacing: "0.5px" }}>
                <i className="mdi mdi-information-outline me-2 font-size-15 text-primary"></i>
                Configurações da Conta
            </h6>

            <Row className="mb-4">
                <Col md={8} className="mb-3">
                    <NeoInput
                        label="Nome da Conta / Identificação *"
                        name="description"
                        flat={true}
                        placeholder="Ex: Conta Principal PJ, Caixa Interno..."
                        error={formik.touched.description && formik.errors.description ? formik.errors.description : ""}
                        {...formik.getFieldProps("description")}
                    />
                </Col>

                <Col md={4} className="mb-3">
                    <NeoInput
                        label="Status"
                        name="status"
                        type="select"
                        flat={true}
                        {...formik.getFieldProps("status")}
                    >
                        <option value={GLOBAL_STATUS.ACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.ACTIVE]} (Em uso)</option>
                        <option value={GLOBAL_STATUS.INACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.INACTIVE]} (Ignorar)</option>
                    </NeoInput>
                </Col>

                <Col md={6} className="mb-3">
                    <NeoInput
                        label="Banco"
                        name="bankName"
                        type="select"
                        flat={true}
                        error={formik.touched.bankName && formik.errors.bankName ? formik.errors.bankName : ""}
                        {...formik.getFieldProps("bankName")}
                    >
                        <option value="">Selecione um banco...</option>
                        {BANK_OPTIONS.map(bank => (
                            <option key={bank} value={bank}>{bank}</option>
                        ))}
                    </NeoInput>
                </Col>

                <Col md={6} className="mb-3">
                    <NeoInput
                        label="Tipo de Conta"
                        name="accountType"
                        type="select"
                        flat={true}
                        {...formik.getFieldProps("accountType")}
                    >
                        {ACCOUNT_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                    </NeoInput>
                </Col>

                <Col md={4} className="mb-3">
                    <NeoInput
                        label="Agência"
                        name="agency"
                        flat={true}
                        placeholder="0001"
                        className="text-center font-monospace"
                        {...formik.getFieldProps("agency")}
                    />
                </Col>

                <Col md={4} className="mb-3">
                    <NeoInput
                        label="Nº Conta"
                        name="accountNumber"
                        flat={true}
                        placeholder="12345-6"
                        className="text-center font-monospace"
                        {...formik.getFieldProps("accountNumber")}
                    />
                </Col>

                <Col md={4} className="mb-3">
                    <NeoInput
                        label="Saldo Inicial (R$) *"
                        name="initialBalance"
                        type="number"
                        step="0.01"
                        flat={true}
                        placeholder="0,00"
                        disabled={!!account.id}
                        className="fw-bold text-primary text-end"
                        error={formik.touched.initialBalance && formik.errors.initialBalance ? formik.errors.initialBalance : ""}
                        {...formik.getFieldProps("initialBalance")}
                    />
                    <small className="text-muted font-size-11">Saldo disponível no momento do cadastro.</small>
                </Col>
            </Row>
        </NeoCard>
    );
};

BankAccountEditor.propTypes = {
    account: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default BankAccountEditor;
